import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faNewspaper,
  faBullhorn,
  faCreditCard,
  faExclamationTriangle,
  faShieldAlt,
  faPhone,
  faEnvelope,
  faGlobe,
  faCheckCircle,
  faStar,
  faHeart,
  faRocket,
  faEye,
  faLock,
  faDatabase,
  faChild,
  faCog,
  faLink
} from '@fortawesome/free-solid-svg-icons';

const PrivacyPolicy = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 },
    transition: { duration: 0.6 }
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const staggerItem = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 20 },
    transition: { duration: 0.5 }
  };

  const privacySections = [
    {
      id: 1,
      title: "Information We Collect",
      icon: faDatabase,
      color: "from-blue-500 to-blue-600",
      bgColor: "from-blue-50 to-blue-100",
      borderColor: "border-blue-200",
      items: [
        "Full Name",
        "Phone Number",
        "Email Address",
        "Residential Address",
        "Identity Verification Documents (e.g., PAN Card, Aadhaar Card – only for verification purposes)",
        "Payment Details (UPI ID or Bank Account information for payouts)"
      ]
    },
    {
      id: 2,
      title: "How We Use Your Information",
      icon: faCog,
      color: "from-green-500 to-green-600",
      bgColor: "from-green-50 to-green-100",
      borderColor: "border-green-200",
      items: [
        "User identity verification (KYC)",
        "Processing secure payments through Razorpay Payment Gateway",
        "Sending verified advertisements to reporters",
        "Helping advertisers with better marketing reach",
        "Managing user accounts (view, edit, or delete information)",
        "Compliance with legal obligations and fraud prevention"
      ]
    },
    {
      id: 3,
      title: "Payments & Transactions",
      icon: faCreditCard,
      color: "from-purple-500 to-purple-600",
      bgColor: "from-purple-50 to-purple-100",
      borderColor: "border-purple-200",
      items: [
        "All transactions are processed securely via Razorpay Payment Gateway.",
        "For payouts, we collect your UPI ID or Bank Account details.",
        "We do not store or misuse sensitive payment information."
      ]
    },
    {
      id: 4,
      title: "Data Security",
      icon: faShieldAlt,
      color: "from-emerald-500 to-emerald-600",
      bgColor: "from-emerald-50 to-emerald-100",
      borderColor: "border-emerald-200",
      items: [
        "Your personal information is stored securely and protected from unauthorized access.",
        "We do not sell, rent, or share your data with any third party for marketing purposes.",
        "Users have full control to view, update, or delete their information at any time."
      ]
    },
    {
      id: 5,
      title: "Third-Party Services",
      icon: faLink,
      color: "from-orange-500 to-orange-600",
      bgColor: "from-orange-50 to-orange-100",
      borderColor: "border-orange-200",
      items: [
        "Our platform may integrate services from third-party providers such as Facebook, YouTube, and Instagram.",
        "These third parties may use their own cookies or tracking technologies.",
        "We are not responsible for their privacy practices, and users are advised to review their respective privacy policies."
      ]
    },
    {
      id: 6,
      title: "Children's Privacy & Prohibited Activities",
      icon: faChild,
      color: "from-pink-500 to-pink-600",
      bgColor: "from-pink-50 to-pink-100",
      borderColor: "border-pink-200",
      items: [
        "IINSAF is designed as a safe and responsible platform, also suitable for children.",
        "Nudity, fraud, scams, illegal online gaming, or objectionable content are strictly prohibited.",
        "Any individual found engaging in such activities will be solely responsible and may face legal action."
      ],
      warning: "Prohibited activities may result in legal action."
    },
    {
      id: 7,
      title: "Changes to This Policy",
      icon: faStar,
      color: "from-yellow-500 to-yellow-600",
      bgColor: "from-yellow-50 to-yellow-100",
      borderColor: "border-yellow-200",
      items: [
        "We reserve the right to update or modify this Privacy Policy at any time without prior notice.",
        "The latest version will always be available on our website."
      ]
    },
    {
      id: 8,
      title: "Contact Us",
      icon: faPhone,
      color: "from-cyan-500 to-cyan-600",
      bgColor: "from-cyan-50 to-cyan-100",
      borderColor: "border-cyan-200",
      items: [
        "Phone: 9992396623",
        "Email: iinsafofficial@gmail.com",
        "Website: www.iinsaf.com"
      ]
    }
  ];

  return (
    <div className="min-h-screen w-full bg-gradient-to-br from-slate-50 via-white to-blue-50 pt-20 pb-8 px-2 sm:px-4">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: isVisible ? 1 : 0, scale: isVisible ? 1 : 0.95 }}
        transition={{ duration: 0.8 }}
        className="w-full max-w-7xl mx-auto mb-12"
      >
        <div className="bg-white/80 backdrop-blur-lg rounded-3xl shadow-2xl border border-white/20 p-8 sm:p-12 md:p-16 lg:p-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center mb-8"
          >
            <div className="inline-block bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4 shadow-lg">
              PRIVACY POLICY
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent">
                Privacy Policy
              </span>{' '}
              – IINSAF
            </h1>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-500 to-blue-600 mx-auto rounded-full mb-6"></div>
            <p className="text-lg sm:text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Welcome to{' '}
              <span className="font-semibold text-blue-600">IINSAF</span>. 
              We are committed to protecting the privacy and security of our users, including{' '}
              <span className="font-semibold text-green-600">Reporters</span>,{' '}
              <span className="font-semibold text-purple-600">Advertisers</span>, and{' '}
              <span className="font-semibold text-orange-600">Readers</span>. 
              This Privacy Policy explains how we collect, use, and safeguard your personal information when you use our platform.
            </p>
          </motion.div>
        </div>
      </motion.div>

      {/* Privacy Policy Section */}
      <div className="w-full max-w-7xl mx-auto">
        <motion.div
          variants={staggerContainer}
          initial="initial"
          animate="animate"
          className="space-y-6"
        >
          {privacySections.map((section, index) => (
            <motion.div
              key={section.id}
              variants={staggerItem}
              className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden hover:shadow-2xl transition-all duration-300"
            >
              <div className={`p-6 sm:p-8 bg-gradient-to-r ${section.bgColor} border-b ${section.borderColor}`}>
                <div className="flex items-center mb-6">
                  <div className={`w-12 h-12 bg-gradient-to-r ${section.color} rounded-xl flex items-center justify-center mr-4 shadow-lg`}>
                    <FontAwesomeIcon icon={section.icon} className="text-white text-xl" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-800">
                    {section.id}. {section.title}
                  </h2>
                </div>
                
                <div className="space-y-3">
                  {section.items.map((item, itemIndex) => (
                    <motion.div
                      key={itemIndex}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.3, delay: itemIndex * 0.1 }}
                      className="flex items-start"
                    >
                      <FontAwesomeIcon 
                        icon={faCheckCircle} 
                        className={`text-${section.color.split('-')[1]}-500 mt-1 mr-3 flex-shrink-0`} 
                      />
                      <span className="text-gray-700 leading-relaxed">{item}</span>
                    </motion.div>
                  ))}
                  
                  {section.warning && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.3, delay: section.items.length * 0.1 }}
                      className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl"
                    >
                      <div className="flex items-center">
                        <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-500 mr-3" />
                        <span className="text-red-700 font-semibold">{section.warning}</span>
                      </div>
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: isVisible ? 1 : 0, y: isVisible ? 0 : 30 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-16 bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl shadow-xl p-6 sm:p-8 text-white"
        >
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center mr-4 shadow-lg">
              <FontAwesomeIcon icon={faHeart} className="text-white text-xl" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold">Privacy Questions?</h2>
          </div>
          <p className="text-white/90 mb-6 text-lg">
            Have questions about your privacy or data protection? Our support team is here to help with any concerns.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="flex items-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <FontAwesomeIcon icon={faPhone} className="text-white text-xl mr-4" />
              <div>
                <p className="font-semibold">Phone</p>
                <a href="tel:9992396623" className="text-white/90 hover:text-white transition-colors duration-300">
                  9992396623
                </a>
              </div>
            </div>
            <div className="flex items-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <FontAwesomeIcon icon={faEnvelope} className="text-white text-xl mr-4" />
              <div>
                <p className="font-semibold">Email</p>
                <a href="mailto:iinsafofficial@gmail.com" className="text-white/90 hover:text-white transition-colors duration-300">
                  iinsafofficial@gmail.com
                </a>
              </div>
            </div>
            <div className="flex items-center p-4 bg-white/10 backdrop-blur-sm rounded-xl border border-white/20">
              <FontAwesomeIcon icon={faGlobe} className="text-white text-xl mr-4" />
              <div>
                <p className="font-semibold">Website</p>
                <a href="https://www.iinsaf.com" className="text-white/90 hover:text-white transition-colors duration-300" target="_blank" rel="noopener noreferrer">
                  www.iinsaf.com
                </a>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;